/**
 * Render `@[[nodeId]]` markers in read-only text (Director markdown, question
 * cards) as the canonical `ReadOnlyMentionChip`.
 *
 * The Director refers to every node as `@[[nodeId]]` (Workplace post
 * 1631922445231719); the user must never see the raw ID. Resolution data
 * (candidates, project, click handler) flows through context so
 * `MemoizedMarkdown` keeps its single-string memo boundary — only the chips
 * re-render when the canvas changes, not the markdown parse.
 */
import { createContext, useContext, type ReactNode } from 'react'
import type { MentionCandidate } from '../canvas-v2/nodes/shared/mentionUtils'
import { ReadOnlyMentionChip } from './MentionChip'
import { createNadDebug } from '../../utils/debugLog'

const directorChipLog = createNadDebug('dw:chat:director-mention-chip')

export const MENTION_MARKER_RE = /@\[\[([^\]]+)\]\]/g

export interface MentionRenderContextValue {
  candidates: readonly MentionCandidate[]
  projectId?: string
  onMentionClick?: (nodeId: string) => void
  focusLabel: string
  /** Label for a chip whose node is no longer on the canvas — never the raw ID. */
  removedLabel: string
}

const MentionRenderContext = createContext<MentionRenderContextValue | null>(null)

export function MentionRenderProvider({
  value,
  children,
}: {
  value: MentionRenderContextValue
  children: ReactNode
}) {
  return <MentionRenderContext.Provider value={value}>{children}</MentionRenderContext.Provider>
}

/** Resolve a node id to its chip candidate; unknown ids get a neutral label, never the id. */
export function resolveMentionCandidate(
  nodeId: string,
  candidates: readonly MentionCandidate[],
  removedLabel: string,
): MentionCandidate {
  return candidates.find((item) => item.id === nodeId) ?? {
    id: nodeId,
    type: 'unknown',
    label: removedLabel,
    icon: '',
  }
}

interface ChipRenderProps {
  surface: string
  /** False inside a clickable parent (question option) so the parent owns the click. */
  interactive?: boolean
}

export function ResolvedMentionChip({
  nodeId,
  surface,
  interactive = true,
}: ChipRenderProps & { nodeId: string }) {
  const context = useContext(MentionRenderContext)
  const candidates = context?.candidates ?? []
  const candidate = resolveMentionCandidate(nodeId, candidates, context?.removedLabel ?? '')
  directorChipLog({
    event: 'director-chip-render',
    surface,
    resolved: candidate.type !== 'unknown',
  })
  return (
    <ReadOnlyMentionChip
      candidate={candidate}
      projectId={context?.projectId}
      testId={`director-mention-chip-${nodeId}`}
      onActivate={interactive ? context?.onMentionClick : undefined}
      title={context?.focusLabel ?? ''}
    />
  )
}

/** Split plain text around `@[[id]]` markers, rendering each marker as a chip. */
export function MentionMarkupText({
  text,
  surface,
  interactive = true,
}: ChipRenderProps & { text: string }) {
  const parts: ReactNode[] = []
  let lastIndex = 0
  for (const match of text.matchAll(MENTION_MARKER_RE)) {
    const index = match.index ?? 0
    if (index > lastIndex) parts.push(text.slice(lastIndex, index))
    parts.push(<ResolvedMentionChip
        key={`m-${index}`}
        nodeId={match[1]}
        surface={surface}
        interactive={interactive}
      />)
    lastIndex = index + match[0].length
  }
  if (parts.length === 0) return <>{text}</>
  if (lastIndex < text.length) parts.push(text.slice(lastIndex))
  return <>{parts}</>
}

// ─── remark plugin ───────────────────────────────────────────────────────────

/** Structural subset of mdast nodes this plugin touches. */
interface MdNode {
  type: string
  value?: string
  children?: MdNode[]
  data?: { hName?: string; hProperties?: Record<string, string> }
}

export const MENTION_NODE_ID_PROPERTY = 'dataMentionNodeId'

function splitTextNode(node: MdNode): MdNode[] | null {
  const value = node.value ?? ''
  const pieces: MdNode[] = []
  let lastIndex = 0
  for (const match of value.matchAll(MENTION_MARKER_RE)) {
    const index = match.index ?? 0
    if (index > lastIndex) pieces.push({ type: 'text', value: value.slice(lastIndex, index) })
    pieces.push({
      type: 'mentionChip',
      data: { hName: 'span', hProperties: { [MENTION_NODE_ID_PROPERTY]: match[1] } },
    })
    lastIndex = index + match[0].length
  }
  if (pieces.length === 0) return null
  if (lastIndex < value.length) pieces.push({ type: 'text', value: value.slice(lastIndex) })
  return pieces
}

function visit(node: MdNode): void {
  if (!node.children) return
  const next: MdNode[] = []
  for (const child of node.children) {
    const split = child.type === 'text' ? splitTextNode(child) : null
    if (split) {
      next.push(...split)
    } else {
      visit(child)
      next.push(child)
    }
  }
  node.children = next
}

/**
 * Turn `@[[id]]` in markdown text into `<span data-mention-node-id>` elements.
 * Inline code is left alone so a quoted marker stays literal.
 */
export function remarkMentionChips() {
  return (tree: MdNode) => visit(tree)
}
